import { type DataTableColumnDef } from 'components/data-table';
import { PlainLink } from 'components/link';
import { parseLocalDateTime } from 'utils/dates';
import {
  TypeCurveVerificationStatusIndicator,
  typeCurveVerificationIsDecided,
} from 'components/type-curve-verification-status/type-curve-verification-status';
import type { TypeCurveWorkspaceListQueryResponse } from 'types/api-responses';
import styles from './type-curves-listing.module.scss';

type WsRow = TypeCurveWorkspaceListQueryResponse & Record<string, unknown>;

export const typeCurveWorkspacesColumns: DataTableColumnDef<WsRow>[] = [
  {
    accessorKey: 'workspace',
    header: 'Workspace',
    size: 220,
    enableSorting: true,
    cell: ({ row, getValue }) => (
      <PlainLink
        href={row.original.href}
        openInNewTab
        onClick={(e) => e.stopPropagation()}
      >
        {getValue() as string}
      </PlainLink>
    ),
  },
  {
    accessorKey: 'uniqueTcId',
    header: 'Unique TC ID',
    size: 500,
    enableSorting: true,
    cell: ({ row, getValue }) => {
      const name = (getValue() as string | null) ?? '--';
      const resources = row.original.resources;
      const href =
        resources?.typeCurve?.href ?? resources?.typeCurveHeader?.href;
      if (!href) return name;
      return (
        <PlainLink href={href} onClick={(e) => e.stopPropagation()}>
          {name}
        </PlainLink>
      );
    },
  },
  {
    accessorKey: 'typeCurveScenario',
    header: 'Type Curve Scenario',
    size: 180,
    enableSorting: true,
    meta: { type: 'enum' },
    cell: ({ row, getValue }) => {
      const name = (getValue() as string | null) ?? '--';
      const href = row.original.resources?.typeCurveScenario?.href;
      if (!href) return name;
      return (
        <PlainLink href={href} onClick={(e) => e.stopPropagation()}>
          {name}
        </PlainLink>
      );
    },
  },
  {
    accessorKey: 'createdBy',
    header: 'Created By',
    size: 140,
    enableSorting: true,
    meta: { type: 'enum' },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    size: 140,
    enableSorting: true,
    meta: { type: 'date' },
    cell: ({ getValue }) => {
      const value = getValue() as string | null;
      return value ? parseLocalDateTime(value) : '--';
    },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Last Updated',
    size: 140,
    enableSorting: true,
    meta: { type: 'date' },
    cell: ({ getValue }) => {
      const value = getValue() as string | null;
      return value ? parseLocalDateTime(value) : '--';
    },
  },
  {
    id: 'tasks',
    header: 'Tasks',
    size: 100,
    enableSorting: false,
    cell: ({ row }) => {
      const { completed, total } = row.original.tasks;
      return `${completed}/${total}`;
    },
  },
  {
    accessorKey: 'promotedBy',
    header: 'Promoted By',
    size: 150,
    enableSorting: false,
    meta: { type: 'enum' },
    cell: ({ getValue }) => (getValue() as string | null) ?? '—',
  },
  {
    accessorKey: 'verification',
    header: 'Verification',
    size: 150,
    enableSorting: false,
    cell: ({ getValue }) => {
      const status = getValue() as string | null | undefined;
      if (!typeCurveVerificationIsDecided(status)) return '—';
      return (
        <TypeCurveVerificationStatusIndicator
          status={status}
          showLabel
          className={styles.verificationStatusCell}
          iconClasses={{
            correct: styles.verificationStatusCorrect,
            incorrect: styles.verificationStatusIncorrect,
          }}
        />
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 140,
    enableSorting: false,
    meta: { type: 'enum' },
    cell: ({ getValue }) => (getValue() as string | null) ?? '—',
  },
  {
    accessorKey: 'promotedAt',
    header: 'Promoted At',
    size: 140,
    enableSorting: false,
    meta: { type: 'date' },
    cell: ({ getValue }) => {
      const value = getValue() as string | null;
      return value ? parseLocalDateTime(value) : '—';
    },
  },
  {
    id: 'unitsVerified',
    header: 'Units Verified',
    size: 140,
    enableSorting: false,
    cell: ({ row }) => {
      const uv = row.original.unitsVerified;
      if (!uv) return '—';
      return `${uv.verified}/${uv.total}`;
    },
  },
];
